import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Table, Select, Tag, Button, Typography, Card, Space, Spin, Empty, Popconfirm, message, Input } from 'antd';
import { UserOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { getAllUsers, updateUserRole, deleteUser, User, UserRole } from '../../services/adminUserService';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;

export default function UsersPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [updating, setUpdating] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadUsers();
      }
    });
    return unsubscribe;
  }, [navigate]);

  const loadUsers = async () => {
    try {
      const usersData = await getAllUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    setUpdating(userId);
    try {
      await updateUserRole(userId, newRole);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      message.success('Função atualizada com sucesso');
    } catch (error) {
      console.error('Error updating role:', error);
      message.error('Falha ao atualizar função do usuário');
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (userId: string) => {
    setUpdating(userId);
    try {
      await deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      message.success('Usuário excluído com sucesso');
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('Falha ao excluir usuário');
    } finally {
      setUpdating(null);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchText.trim()) return users;
    const search = searchText.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(search) ||
        u.email?.toLowerCase().includes(search) ||
        u.id.toLowerCase().includes(search)
    );
  }, [users, searchText]);

  const columns: ColumnsType<User> = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text strong>{name || '-'}</Text>,
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Função',
      dataIndex: 'role',
      key: 'role',
      render: (role, record) => (
        <Select
          value={role}
          onChange={(value) => handleRoleChange(record.id, value as UserRole)}
          loading={updating === record.id}
          disabled={updating === record.id}
          style={{ width: 130 }}
          options={[
            { value: 'attendee', label: <Tag color={colors.info}>Participante</Tag> },
            { value: 'vendor', label: <Tag color={colors.success}>Vendedor</Tag> },
            { value: 'admin', label: <Tag color={colors.error}>Admin</Tag> },
          ]}
        />
      ),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'Vendedor', value: 'vendor' },
        { text: 'Participante', value: 'attendee' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'ID da Carteira',
      dataIndex: 'walletId',
      key: 'walletId',
      render: (id) => (
        id ? (
          <Text code style={{ fontSize: 12 }}>
            {id.substring(0, 8)}...
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Excluir usuário"
          description={`Tem certeza que deseja excluir ${record.email}?`}
          onConfirm={() => handleDelete(record.id)}
          okText="Sim"
          cancelText="Não"
          okButtonProps={{ danger: true }}
        >
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={updating === record.id}
          >
            Excluir
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 12 }} />
          Gerenciamento de Usuários
        </Title>
        <Space>
          <Tag color={colors.error}>Admin</Tag>
          <Tag color={colors.success}>Vendedor</Tag>
          <Tag color={colors.info}>Participante</Tag>
        </Space>
      </div>

      <Card style={{ background: colors.cardBackground }}>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Buscar por nome, e-mail ou ID"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </Space>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: <Empty description="Nenhum usuário encontrado" />,
          }}
        />
      </Card>
    </Layout>
  );
}
